import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api, { getImageUrl } from '../../utils/api';
import Loader from '../../components/common/Loader';
import BadgeStatus from '../../components/common/BadgeStatus';
import HandoverChecklist from '../../components/handover/HandoverChecklist';
import HandoverCommentsBox from '../../components/handover/HandoverCommentsBox';
import IssueDescriptionModal from '../../components/modals/IssueDescriptionModal';
import '../../styles/Forms.css';
import '../../styles/Handover.css';

const EmployeeAssetDetailPage = () => {
  const { handoverId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [handover, setHandover] = useState(null);
  const [asset, setAsset] = useState(null);
  const [received, setReceived] = useState(false);
  const [inspected, setInspected] = useState(false);
  const [responsible, setResponsible] = useState(false);
  const [comments, setComments] = useState('');
  const [issues, setIssues] = useState('');
  const [actionType, setActionType] = useState(null); // 'confirm' or 'report'
  const [showIssueModal, setShowIssueModal] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0); // Index for main image display
  const [lightboxIndex, setLightboxIndex] = useState(null); // Index for lightbox modal

  useEffect(() => {
    fetchHandoverDetails();
  }, [handoverId]);

  // Keyboard navigation for image lightbox
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (lightboxIndex === null || !asset?.photos || asset.photos.length === 0) return;

      if (e.key === 'Escape') {
        setLightboxIndex(null);
      } else if (e.key === 'ArrowLeft' && lightboxIndex > 0) {
        setLightboxIndex(lightboxIndex - 1);
      } else if (e.key === 'ArrowRight' && lightboxIndex < asset.photos.length - 1) {
        setLightboxIndex(lightboxIndex + 1);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxIndex, asset]);

  const fetchHandoverDetails = async () => {
    try {
      setLoading(true);
      
      // Fetch handover details
      const handoverResponse = await api.getHandover(handoverId);
      if (handoverResponse.success) {
        setHandover(handoverResponse.data);
        const assetData = handoverResponse.data.asset || {};
        setAsset(assetData);
        // Initialize selected image index to 0 when asset loads
        if (assetData.photos && assetData.photos.length > 0) {
          setSelectedImageIndex(0);
        }
      } else {
        toast.error('Handover not found');
        navigate('/employee/my-asset-handover');
      }
    } catch (error) {
      console.error('Error fetching handover details:', error);
      toast.error('Error loading asset details');
      navigate('/employee/my-asset-handover');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusLabel = (status) => {
    if (status === 'acknowledged' || status === 'received') return 'Received';
    return 'pending';
  };

  const handleConfirmReceived = async () => {
    if (!received || !inspected || !responsible) {
      toast.error('Please confirm all checklist items');
      return;
    }

    try {
      setSaving(true);
      setActionType('confirm');
      
      await api.acknowledgeHandover(handoverId, {
        received: true,
        inspected: true,
        comments: comments,
      });

      toast.success('Asset confirmed successfully!');
      setReceived(false);
      setInspected(false);
      setResponsible(false);
      setComments('');
      setIssues('');
      setActionType(null);
      
      // Refresh handover data
      const handoverResponse = await api.getHandover(handoverId);
      if (handoverResponse.success) {
        setHandover(handoverResponse.data);
        setAsset(handoverResponse.data.asset || {});
      }
    } catch (error) {
      console.error('Error acknowledging handover:', error);
      toast.error('Error confirming asset: ' + (error.message || 'Unknown error'));
    } finally {
      setSaving(false);
      setActionType(null);
    }
  };

  const handleReportIssue = async (issueText) => {
    const finalIssues = (issueText !== undefined ? issueText : issues || '').trim();
    if (!finalIssues) {
      toast.error('Please describe the issue');
      return;
    }

    try {
      setSaving(true);
      setActionType('report');
      
      await api.acknowledgeHandover(handoverId, {
        received: received,
        inspected: inspected,
        issues: finalIssues,
        comments: comments,
      });

      toast.success('Issue reported successfully! Admin will be notified.');
      setReceived(false);
      setInspected(false);
      setResponsible(false);
      setComments('');
      setIssues('');
      setActionType(null);
      
      // Refresh handover data
      const handoverResponse = await api.getHandover(handoverId);
      if (handoverResponse.success) {
        setHandover(handoverResponse.data);
        setAsset(handoverResponse.data.asset || {});
      }
    } catch (error) {
      console.error('Error reporting issue:', error);
      toast.error('Error reporting issue: ' + (error.message || 'Unknown error'));
    } finally {
      setSaving(false);
      setActionType(null);
    }
  };

  if (loading && !handover) {
    return <Loader />;
  }

  if (!handover || !asset) {
    return (
      <div style={{ padding: '16px' }}>
        <div style={{
          background: 'white',
          padding: '40px',
          borderRadius: '8px',
          textAlign: 'center',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '12px' }}>❌</div>
          <h3 style={{ margin: '0 0 6px 0', fontSize: '18px', fontWeight: 600, color: '#2d3748' }}>
            Asset Not Found
          </h3>
          <p style={{ margin: '0 0 20px 0', color: '#718096', fontSize: '13px' }}>
            The asset you're looking for doesn't exist or you don't have access to it.
          </p>
          <button
            onClick={() => navigate(-1)}
            style={{
              padding: '8px 16px',
              background: 'linear-gradient(rgb(26, 32, 44) 0%, rgb(45, 55, 72) 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '7px',
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: '12px',
            }}
          >
            ← Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="asset-detail-page" style={{ padding: '16px' }}>
      <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
        <button
          onClick={() => navigate(-1)}
          style={{
            padding: '8px 16px',
            background: 'linear-gradient(rgb(26, 32, 44) 0%, rgb(45, 55, 72) 100%)',
            color: 'white',
            border: 'none',
            borderRadius: '7px',
            cursor: 'pointer',
            fontWeight: 600,
            fontSize: '12px',
          }}
        >
          ← Back
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
        {/* Left Column - Basic Asset Info */}
        <div style={{
          background: 'white',
          padding: '20px',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <h2 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: 600 }}>Asset Information</h2>

          <div style={{ display: 'grid', gap: '16px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#718096', marginBottom: '4px' }}>
                Asset Tag
              </label>
              <div style={{ fontSize: '16px', fontWeight: 700, color: '#2d3748' }}>
                {asset.assetTag || 'N/A'}
              </div>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#718096', marginBottom: '4px' }}>
                Description
              </label>
              <div style={{ fontSize: '14px', color: '#4a5568' }}>
                {asset.description || 'N/A'}
              </div>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#718096', marginBottom: '4px' }}>
                Brand / Model / Serial No
              </label>
              <div style={{ fontSize: '14px', color: '#4a5568' }}>
                {asset.brand || 'N/A'} {asset.model ? ` / ${asset.model}` : ''} {asset.serialNumber ? ` / ${asset.serialNumber}` : ''}
              </div>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#718096', marginBottom: '4px' }}>
                Category
              </label>
              <div style={{ fontSize: '14px', color: '#4a5568' }}>
                {asset.category || 'N/A'}
              </div>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#718096', marginBottom: '4px' }}>
                Status
              </label>
              <div>
                <BadgeStatus status={asset.status || 'pending'} />
              </div>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#718096', marginBottom: '4px' }}>
                Condition
              </label>
              <div style={{ fontSize: '14px', color: '#4a5568' }}>
                {asset.condition || 'N/A'}
              </div>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#718096', marginBottom: '4px' }}>
                Site & Location
              </label>
              <div style={{ fontSize: '14px', color: '#4a5568' }}>
                {asset.location?.site?.name || 'N/A'} {asset.location?.locationDetail ? ` / ${asset.location.locationDetail}` : ''}
              </div>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#718096', marginBottom: '4px' }}>
                Department
              </label>
              <div style={{ fontSize: '14px', color: '#4a5568' }}>
                {asset.location?.department?.name || 'N/A'}
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - Images & Assignment & Acknowledgement Info */}
        <div style={{
          background: 'white',
          padding: '20px',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          display: 'flex',
          flexDirection: 'column',
          gap: '20px'
        }}>
          {/* Asset Photos Section */}
          <div>
            <h2 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: 600 }}>Asset Photos</h2>
            
            {asset.photos && asset.photos.length > 0 ? (
              <div>
                {/* Main Image Display */}
                <div style={{ marginBottom: '12px', textAlign: 'center' }}>
                  <img
                    src={getImageUrl(asset.photos[selectedImageIndex].url)}
                    alt={asset.description}
                    onClick={() => {
                      // Open lightbox when clicking main image
                      setLightboxIndex(selectedImageIndex);
                    }}
                    style={{
                      width: '100%',
                      maxHeight: '350px',
                      objectFit: 'contain',
                      borderRadius: '8px',
                      border: '1px solid #e2e8f0',
                      cursor: 'pointer',
                      transition: 'transform 0.2s ease'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.02)'}
                    onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                  />
                </div>
                
                {/* Thumbnail Gallery - Show for all images (even single image) */}
                {asset.photos.length > 0 && (
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(70px, 1fr))',
                    gap: '8px',
                    padding: '12px',
                    background: '#f7fafc',
                    borderRadius: '6px',
                    border: '1px solid #e2e8f0'
                  }}>
                    {asset.photos.map((photo, index) => (
                      <div
                        key={index}
                        onClick={() => {
                          setSelectedImageIndex(index);
                          // Also open lightbox when clicking thumbnail
                          setLightboxIndex(index);
                        }}
                        style={{
                          position: 'relative',
                          cursor: 'pointer',
                          border: selectedImageIndex === index ? '3px solid #667eea' : '2px solid #e2e8f0',
                          borderRadius: '6px',
                          overflow: 'hidden',
                          transition: 'all 0.2s ease',
                          opacity: selectedImageIndex === index ? 1 : 0.8
                        }}
                        onMouseEnter={(e) => {
                          if (selectedImageIndex !== index) {
                            e.currentTarget.style.opacity = '1';
                            e.currentTarget.style.transform = 'scale(1.05)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (selectedImageIndex !== index) {
                            e.currentTarget.style.opacity = '0.8';
                            e.currentTarget.style.transform = 'scale(1)';
                          }
                        }}
                      >
                        <img
                          src={getImageUrl(photo.url)}
                          alt={`${asset.description} - Photo ${index + 1}`}
                          style={{
                            width: '100%',
                            height: '70px',
                            objectFit: 'cover',
                            display: 'block'
                          }}
                        />
                        {selectedImageIndex === index && (
                          <div style={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            background: 'linear-gradient(to top, rgba(102, 126, 234, 0.8), transparent)',
                            padding: '4px',
                            fontSize: '10px',
                            color: 'white',
                            fontWeight: 600,
                            textAlign: 'center'
                          }}>
                            {index + 1}/{asset.photos.length}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Photo Count Badge */}
                <div style={{
                  marginTop: '8px',
                  textAlign: 'center',
                  fontSize: '12px',
                  color: '#718096'
                }}>
                  {asset.photos.length} photo{asset.photos.length !== 1 ? 's' : ''} available
                </div>
              </div>
            ) : (
              <div style={{
                padding: '40px',
                background: '#f7fafc',
                borderRadius: '8px',
                textAlign: 'center',
                color: '#a0aec0'
              }}>
                <div style={{ fontSize: '48px', marginBottom: '8px' }}>🖼️</div>
                <div style={{ fontSize: '13px' }}>No photos available</div>
              </div>
            )}
          </div>

          {/* Assignment & Acknowledgement Section */}
          <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: '20px' }}>
          <h2 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: 600 }}>Assignment & Acknowledgement</h2>
          
            <div style={{ display: 'grid', gap: '16px', marginBottom: handover?.acknowledgementStatus === 'pending' ? '24px' : '0' }}>
            <div>
              <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#718096', marginBottom: '4px' }}>
                Assigned Date
              </label>
              <div style={{ fontSize: '14px', color: '#4a5568' }}>
                {formatDate(handover.assignedDate)}
              </div>
            </div>

            {handover.expectedReturnDate && (
              <div>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#718096', marginBottom: '4px' }}>
                  Expected Return Date
                </label>
                <div style={{ fontSize: '14px', color: '#4a5568' }}>
                  {formatDate(handover.expectedReturnDate)}
                </div>
              </div>
            )}

            <div>
              <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#718096', marginBottom: '4px' }}>
                Assigned By
              </label>
              <div style={{ fontSize: '14px', color: '#4a5568', fontWeight: 600 }}>
                {handover.assignedBy?.name || 'N/A'}
              </div>
              {handover.assignedBy?.email && (
                <div style={{ fontSize: '12px', color: '#718096', marginTop: '4px' }}>
                  {handover.assignedBy.email}
                </div>
              )}
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#718096', marginBottom: '4px' }}>
                Acknowledgement Status
              </label>
              <div>
                <BadgeStatus 
                  status={getStatusLabel(handover.acknowledgementStatus)}
                  variant={
                    getStatusLabel(handover.acknowledgementStatus) === 'Received' ? 'info' :
                    getStatusLabel(handover.acknowledgementStatus) === 'pending' ? 'warning' :
                    'danger'
                  }
                />
              </div>
            </div>

            {handover.acknowledgedAt && (
              <div>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#718096', marginBottom: '4px' }}>
                  Last Acknowledged On
                </label>
                <div style={{ fontSize: '14px', color: '#4a5568' }}>
                  {formatDateTime(handover.acknowledgedAt)}
                </div>
              </div>
            )}

            {handover.notes && (
              <div>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#718096', marginBottom: '4px' }}>
                  Notes from Admin
                </label>
                <div style={{ 
                  fontSize: '14px', 
                  color: '#4a5568', 
                  padding: '12px', 
                  background: '#f7fafc', 
                  borderRadius: '6px',
                  border: '1px solid #e2e8f0'
                }}>
                  {handover.notes}
                </div>
              </div>
            )}

            {handover.comments && (
              <div>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#718096', marginBottom: '4px' }}>
                  Your Comments
                </label>
                <div style={{ 
                  fontSize: '14px', 
                  color: '#4a5568', 
                  padding: '12px', 
                  background: '#f7fafc', 
                  borderRadius: '6px',
                  border: '1px solid #e2e8f0'
                }}>
                  {handover.comments}
                </div>
              </div>
            )}

            {handover.issues && (
              <div>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#718096', marginBottom: '4px' }}>
                  Issue Reported
                </label>
                <div style={{ 
                  fontSize: '14px', 
                  color: '#c53030', 
                  padding: '12px', 
                  background: '#fed7d7', 
                  borderRadius: '6px',
                  border: '1px solid #fc8181'
                }}>
                  {handover.issues}
                </div>
              </div>
            )}
          </div>

          {/* Confirm Receipt Section - Only show if pending */}
          {handover.acknowledgementStatus === 'pending' && (
            <>
              <div style={{ 
                borderTop: '1px solid #e2e8f0', 
                paddingTop: '20px',
                marginTop: '20px'
              }}>
                <h2 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: 600 }}>
                  Confirm Receipt
                </h2>

                <div style={{ marginBottom: '16px' }}>
                  <HandoverChecklist
                    received={received}
                    inspected={inspected}
                    responsible={responsible}
                    onReceivedChange={setReceived}
                    onInspectedChange={setInspected}
                    onResponsibleChange={setResponsible}
                  />
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <HandoverCommentsBox
                    value={comments}
                    onCommentChange={setComments}
                    placeholder="Example: Bag is missing one SFP module."
                  />
                </div>

                <div style={{ display: 'flex', gap: '10px', marginBottom: actionType === 'report' ? '16px' : '0' }}>
                  <button
                    onClick={handleConfirmReceived}
                    disabled={saving || !received || !inspected || !responsible}
                    style={{
                      flex: 1,
                      padding: '12px',
                      background: (!received || !inspected || !responsible) ? '#cbd5e0' : 'linear-gradient(135deg, #38a169 0%, #2f855a 100%)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: (!received || !inspected || !responsible) ? 'not-allowed' : 'pointer',
                      fontWeight: 600,
                      fontSize: '14px',
                      opacity: saving && actionType === 'confirm' ? 0.6 : 1
                    }}
                  >
                    {saving && actionType === 'confirm' ? 'Processing...' : '✓ Confirm Received'}
                  </button>

                  <button
                    onClick={() => {
                      if (!issues.trim()) {
                        setShowIssueModal(true);
                      } else {
                        setActionType('report');
                        handleReportIssue(issues);
                      }
                    }}
                    disabled={saving}
                    style={{
                      flex: 1,
                      padding: '12px',
                      background: 'linear-gradient(135deg, #e53e3e 0%, #c53030 100%)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontWeight: 600,
                      fontSize: '14px',
                      opacity: saving && actionType === 'report' ? 0.6 : 1
                    }}
                  >
                    {saving && actionType === 'report' ? 'Processing...' : '⚠ Report Issue'}
                  </button>
                </div>

                {actionType === 'report' && (
                  <div style={{ marginTop: '12px' }}>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 600, color: '#4a5568' }}>
                      Issue Description *
                    </label>
                    <textarea
                      value={issues}
                      onChange={(e) => setIssues(e.target.value)}
                      placeholder="Describe the issue with the asset..."
                      rows="4"
                      required
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: '2px solid #e2e8f0',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontFamily: 'inherit',
                        resize: 'vertical'
                      }}
                    />
                  </div>
                )}
              </div>
            </>
          )}
          </div>
        </div>
      </div>

      {/* Issue Description Modal */}
      <IssueDescriptionModal
        isOpen={showIssueModal}
        onClose={() => setShowIssueModal(false)}
        onSubmit={(issueText) => {
          setIssues(issueText);
          setActionType('report');
          handleReportIssue(issueText);
        }}
      />

      {/* Image Lightbox Modal */}
      {lightboxIndex !== null && asset?.photos && asset.photos.length > 0 && (
        <div
          onClick={() => setLightboxIndex(null)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.9)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
            cursor: 'pointer'
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'relative',
              maxWidth: '90vw',
              maxHeight: '90vh',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center'
            }}
          >
            {/* Close Button */}
            <button
              onClick={() => setLightboxIndex(null)}
              style={{
                position: 'absolute',
                top: '-40px',
                right: '0',
                background: 'rgba(255, 255, 255, 0.2)',
                border: 'none',
                borderRadius: '50%',
                width: '36px',
                height: '36px',
                color: 'white',
                fontSize: '24px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 'bold',
                transition: 'background 0.2s ease'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)'}
            >
              ×
            </button>

            {/* Main Image */}
            <img
              src={getImageUrl(asset.photos[lightboxIndex].url)}
              alt={`${asset.description} - Photo ${lightboxIndex + 1}`}
              style={{
                maxWidth: '100%',
                maxHeight: '80vh',
                objectFit: 'contain',
                borderRadius: '8px',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)'
              }}
            />

            {/* Navigation Arrows */}
            {asset.photos.length > 1 && (
              <>
                {lightboxIndex > 0 && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setLightboxIndex(lightboxIndex - 1);
                    }}
                    style={{
                      position: 'absolute',
                      left: '-50px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'rgba(255, 255, 255, 0.2)',
                      border: 'none',
                      borderRadius: '50%',
                      width: '40px',
                      height: '40px',
                      color: 'white',
                      fontSize: '20px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 'bold',
                      transition: 'background 0.2s ease'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)'}
                  >
                    ‹
                  </button>
                )}
                {lightboxIndex < asset.photos.length - 1 && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setLightboxIndex(lightboxIndex + 1);
                    }}
                    style={{
                      position: 'absolute',
                      right: '-50px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'rgba(255, 255, 255, 0.2)',
                      border: 'none',
                      borderRadius: '50%',
                      width: '40px',
                      height: '40px',
                      color: 'white',
                      fontSize: '20px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 'bold',
                      transition: 'background 0.2s ease'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)'}
                  >
                    ›
                  </button>
                )}
              </>
            )}

            {/* Image Counter */}
            <div style={{
              marginTop: '16px',
              color: 'white',
              fontSize: '14px',
              textAlign: 'center'
            }}>
              {lightboxIndex + 1} / {asset.photos.length}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeAssetDetailPage;

